import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import posthog from 'posthog-js'

import { cn } from '~/shadcn/lib/utils'
import { NotificationBanner } from '~/@mid_ui/notification-banner/NotificationBanner'
import { useUser } from '~/@biz_account/useUser/useUser'
import { FeeNotification } from './fee-notification/FeeNotification'
import { useUserNotifications } from './useUserNotifications'
import { NotificationText } from './NotificationText'

function UserNotifications() {
  const navigate = useNavigate()

  const { notifications, markNotificationAsRead, reloadNotifications } =
    useUserNotifications()

  const { mutate: markNotificationAsReadMutation, isPending } = useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: () => {
      reloadNotifications()
    },
  })
  const { user } = useUser()
  const hasCompanyVerified = user?.profile.adminVerifiedCompany

  const lastNotification = notifications ? notifications[0] : null
  const handleNotificationClose = (notificationId: string) =>
    markNotificationAsReadMutation({ notificationId })

  if (!lastNotification) return null

  const getNotificationMessage = () => {
    if (lastNotification.type === 'registration_finished') {
      return (
        <NotificationText
          title="Gratulujeme k úspešnej registrácii!"
          body={
            <>
              <a
                href="/add-funds"
                onClick={(event) => {
                  event.preventDefault()
                  navigate('/account/add-funds')
                }}
                className={cn(
                  'text-[#121212] border-b border-[#121212] no-underline',
                  'hover:text-[#121212] hover:no-underline'
                )}>
                Vložte si peniaze
              </a>{' '}
              do peňaženky a začnite investovať.
            </>
          }
        />
      )
    }

    if (lastNotification.type === 'admin_company_confirmation') {
      if (!hasCompanyVerified) return null
      return (
        <NotificationText
          title="Vaše firemné údaje boli úspešne overené."
          body=" "
        />
      )
    }

    if (lastNotification.type === 'withdrawal_success') {
      return (
        <NotificationText
          title="Výber bol potvrdený."
          body="Do 1-3 dní peniaze dorazia na vybraný účet"
        />
      )
    }

    if (lastNotification.type === 'order_created') {
      return (
        <NotificationText
          title="Objednávka investície bola podpísaná."
          body="Vaša investícia sa zobrazí v časti Moje investície."
        />
      )
    }

    if (lastNotification.type === 'fee_notice') {
      return <FeeNotification />
    }

    return null
  }

  const message = getNotificationMessage()

  if (!message) {
    posthog.captureException(
      new Error(`Unknown notification type=${lastNotification.type}`)
    )
    return null
  }

  return (
    <NotificationBanner
      disabled={isPending}
      category={lastNotification.category}
      message={message}
      type={lastNotification.type}
      onClick={() => handleNotificationClose(lastNotification._id)}
      closeText={
        lastNotification.type === 'fee_notice' ? 'Beriem na vedomie' : undefined
      }
    />
  )
}

export { UserNotifications }
